import { toast } from 'sonner';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import { describeDevice } from './device';
import { ConfirmDialog, Panel, SessionRow } from './parts';
import { useConfirm } from './useConfirm';
import { useEndOtherSessions, useEndSession, useSessions } from './queries';
import type { Session } from './types';

/**
 * "Where you're signed in": this portal in any browser, and the NuDent app.
 *
 * This browser has no Sign out button here - the sidebar's Sign out is that,
 * and offering it in a list of "other places" invites ending the wrong one.
 */
export function SessionsCard() {
  const sessions = useSessions();
  const endOne = useEndSession();
  const endOthers = useEndOtherSessions();
  const confirm = useConfirm<Session | 'others'>();

  // One clock reading per fetch, so "5 min ago" does not drift between rows.
  const now = sessions.dataUpdatedAt;
  const list = sessions.data ?? [];
  const others = list.filter((session) => !session.current);
  const target = confirm.target;

  return (
    <Panel
      title="Where you're signed in"
      description="This portal and the NuDent app. Sign out anything you don't recognise."
      action={
        others.length > 1 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              confirm.ask('others');
            }}
          >
            Sign out all others
          </Button>
        )
      }
    >
      {sessions.isPending ? (
        <div className="space-y-2 px-[26px] py-4">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-11 w-full bg-muted" />
          ))}
        </div>
      ) : sessions.isError ? (
        <p className="px-[26px] py-6 text-xs text-destructive">
          Your sessions could not be loaded. Try again in a moment.
        </p>
      ) : (
        <ul className="divide-y divide-[#F1F4F8]">
          {/* This browser first, then the rest newest first, as the server sends them. */}
          {[...list.filter((s) => s.current), ...others].map((session) => (
            <SessionRow
              key={session.id}
              session={session}
              now={now}
              current={session.current}
              action={
                !session.current && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => {
                      confirm.ask(session);
                    }}
                  >
                    Sign out
                  </Button>
                )
              }
            />
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={target !== null}
        onOpenChange={(open) => {
          if (!open) confirm.close();
        }}
        title={target === 'others' ? 'Sign out all other devices?' : 'Sign out this device?'}
        description={
          target === 'others'
            ? `Every other browser and NuDent app signed in as you (${String(
                others.length,
              )}) will need your password to sign in again. This browser stays signed in.`
            : target
              ? `${describeDevice(target).label} will need your password to sign in again.`
              : ''
        }
        confirmLabel={target === 'others' ? 'Sign out all' : 'Sign out'}
        pending={endOne.isPending || endOthers.isPending}
        onConfirm={() => {
          if (target === null) return;
          if (target === 'others') {
            endOthers.mutate(undefined, {
              onSuccess: ({ revoked }) => {
                toast.success(
                  `Signed out ${String(revoked)} other ${revoked === 1 ? 'device' : 'devices'}.`,
                );
                confirm.close();
              },
              onError: () => toast.error('Could not sign those devices out. Try again.'),
            });
            return;
          }
          endOne.mutate(target.id, {
            onSuccess: () => {
              toast.success('Signed out.');
              confirm.close();
            },
            onError: () => toast.error('Could not sign that device out. Try again.'),
          });
        }}
      />
    </Panel>
  );
}
